/**
 * Game orchestrator. Owns the fixed-step loop and the game context.
 * See ARCHITECTURE.md §2 for the immutable frame order.
 */

import * as THREE from 'three';
import { EventBus } from './bus.js';
import { Rng } from './rng.js';
import { Input } from './input.js';
import { Profiler } from './profiler.js';
import { CONFIG, SIM_DT, MAX_STEPS_PER_FRAME, MAX_FRAME_DELTA, PHASE } from './tunables.js';
import { SUBSYSTEMS, FIXED_ORDER, UPDATE_ORDER, NullSubsystem, loadSubsystem } from './registry.js';
import { installHarness } from './harness.js';
import { installFallbacks, installDevScaffold, disposeDevScaffold } from './fallbacks.js';

/**
 * The reproducible seed. Every graded shot, the diff gate, the profiler and the tools/
 * probes boot on this exact number — do not change it without re-baselining the shot set.
 */
const DEFAULT_SEED = 20260731;

export class Game {
  constructor(canvas, opts = {}) {
    this.canvas = canvas;
    this.opts = opts;

    const params = new URLSearchParams(location.search);
    this.params = params;

    // Ordered before the seed: a harness boot must keep the fixed default (see below).
    this.isHarness = opts.harness || params.get('harness') === '1';
    this.autoRun = !this.isHarness;

    /**
     * Boot seed. Explicit wins, then harness, then entropy.
     *
     * A harness boot MUST keep the fixed constant. N3 defines determinism as "given a seed",
     * and every graded shot, the diff gate, the profiler and every tools/ probe boot with
     * `harness=1` and depend on this exact number. An explicit `opts.seed` or `?seed=` is
     * likewise honoured verbatim, so nothing reproducible changes.
     *
     * A normal player boot draws from entropy. Without this, every page load of the deployed
     * game replayed one seed, so gameplay's per-round weather fork (`weather:round:0`) always
     * landed on the same answer — seed 20260731 gives nightrain every single time, and the
     * 40/30/30 thaw/winter/nightrain pool was unreachable on a fresh visit.
     *
     * N4 bans `Math.random()`/`Date.now()` in SIMULATION code. This is boot-time config: it
     * runs once, before any subsystem or RNG exists, and its only output is a plain uint32
     * handed to `Rng`. Everything downstream stays a pure function of that number, which is
     * what N3 actually requires. Keep it to this one expression.
     */
    const seedParam = params.get('seed');
    this.seed =
      opts.seed != null ? opts.seed
        : (seedParam !== null && seedParam !== '') ? Number(seedParam)
          : this.isHarness ? DEFAULT_SEED
            : ((Date.now() ^ (Math.random() * 0x100000000)) >>> 0);

    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.systems = new Map();
    this.ctx = null;

    this._accum = 0;
    this._lastTime = 0;
    this._rafId = 0;
    this._running = false;
    this._destroyed = false;
    this._clocksFrozen = false;

    this._assetPromises = [];
    this._resizePending = true;

    this.onError = opts.onError || ((e) => console.error(e));
  }

  // ---------------------------------------------------------------- boot

  async init() {
    this._buildRenderer();
    this._buildContext();

    // Size BEFORE subsystems init so ctx.viewport is real during init(). `ui` scales its
    // HUD from it and would otherwise lay out against a 1x1 viewport. Harmless to call
    // twice — the systems map is empty here, so no onResize is delivered yet.
    this._applySize();

    await this._loadSystems();
    await this._initSystems();
    await this.settleAssets();

    // Hide the boot overlay HERE, not in main.js's .then(). The harness `ready` promise
    // resolves on a rAF fired from installHarness(), which can beat main.js's continuation —
    // leaving the boot splash visible in captured shots. Ordering it here removes the race.
    const bootEl = document.getElementById('boot');
    if (bootEl) { bootEl.hidden = true; bootEl.style.display = 'none'; }

    // Second pass — now the systems exist, so they receive onResize.
    this._resizePending = true;
    this._applySize();
    this.renderOnce();

    if (this.autoRun) this.start();
    return this;
  }

  _buildRenderer() {
    const params = this.params;
    const renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: params.get('aa') !== '0',
      alpha: false,
      powerPreference: 'high-performance',
      stencil: false,
      // Harness needs a stable readable buffer.
      preserveDrawingBuffer: this.isHarness,
      failIfMajorPerformanceCaveat: false,
    });
    // `look` owns everything else about the renderer (colour space, tonemapping, shadows).
    // Core sets only what must exist before any subsystem runs.
    renderer.setPixelRatio(this.isHarness ? 1 : Math.min(window.devicePixelRatio || 1, 2));
    renderer.info.autoReset = false;
    this.renderer = renderer;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(62, 16 / 9, 0.25, 1200);
    this.camera.position.set(0, 4, 10);
    this.camera.lookAt(0, 1, 0);
  }

  _buildContext() {
    const bus = new EventBus();
    const rng = new Rng(this.seed);
    const input = new Input(this.canvas);
    if (!this.isHarness) input.bind();
    const profiler = new Profiler(this.renderer);

    const quality = { ...CONFIG.quality };
    const q = this.params.get('quality');
    if (q) quality.level = q;
    if (quality.level === 'low') { quality.shadows = false; quality.post = false; quality.particleScale = 0.45; quality.trafficScale = 0.5; }
    else if (quality.level === 'medium') { quality.shadows = true; quality.post = true; quality.particleScale = 0.7; quality.trafficScale = 0.75; }

    const debugEnabled = this.params.get('debug') === '1' || !!this.opts.debug;

    this.ctx = {
      game: this,
      scene: this.scene,
      camera: this.camera,
      renderer: this.renderer,
      bus,
      rng,
      time: { sim: 0, elapsed: 0, frame: 0, dt: SIM_DT, alpha: 0, scale: 1 },
      input,
      config: Object.freeze({ ...CONFIG, quality: Object.freeze(quality) }),
      profiler,
      debug: { enabled: debugEnabled, flags: new Set((this.params.get('flags') || '').split(',').filter(Boolean)) },
      seed: this.seed,
      isHarness: this.isHarness,
      viewport: { width: 1, height: 1, aspect: 1, dpr: 1 },

      // Filled in by owners during init(). Consumers must tolerate null until then.
      materials: null,
      world: null,
      player: null,
      gameplay: null,
      fx: null,
      audio: null,
      ui: null,

      /** Register a promise that must resolve before the first shot is captured. */
      assets: {
        register: (p) => { this._assetPromises.push(p); return p; },
        pending: () => this._assetPromises.length,
      },

      /** Subsystem lookup, for the rare legitimate direct call. Prefer the bus. */
      sys: (id) => this.systems.get(id),
    };
  }

  async _loadSystems() {
    // Null-object defaults so every subsystem can call every interface from line one,
    // whether or not the owning subsystem has landed. Owners overwrite these in init().
    installFallbacks(this.ctx);

    for (const entry of SUBSYSTEMS) {
      this.systems.set(entry.id, loadSubsystem(entry, this.ctx));
    }
    // Publish the material library. While `look` has not landed, everyone gets flat
    // placeholder materials so geometry work can proceed — see makeFallbackMaterials.
    const mats = this.systems.get('materials');
    this.ctx.materials = mats && !mats.isNull && !mats.stub ? mats : makeFallbackMaterials();
  }

  async _initSystems() {
    // Boot cost is a real product problem, not just a harness annoyance: a 70 s init is
    // 70 s of the player staring at a loading screen, and the frame profiler cannot see it
    // because it only starts once init() is done. Time every subsystem here and publish it.
    this.initTimings = {};
    const timed = async (id, fn) => {
      const t0 = performance.now();
      try {
        await fn();
      } finally {
        this.initTimings[id] = +(performance.now() - t0).toFixed(1);
      }
    };

    // Materials must be ready before anything builds geometry.
    const mats = this.ctx.materials;
    if (mats && typeof mats.init === 'function') await timed('materials', () => mats.init());

    // `look` next so the renderer is configured and lights exist before world builds.
    const look = this.systems.get('look');
    if (look && !look.isNull) {
      try { await timed('look', () => look.init()); } catch (e) { this.onError(e); }
    }

    for (const [id, sys] of this.systems) {
      if (id === 'materials' || id === 'look') continue;
      if (sys.isNull) continue;
      try {
        await timed(id, () => sys.init());
      } catch (e) {
        console.error(`[game] ${id}.init() failed:`, e);
        this.systems.set(id, new NullSubsystem(id));
        this.onError(e);
      }
    }

    const total = Object.values(this.initTimings).reduce((a, b) => a + b, 0);
    const slow = Object.entries(this.initTimings).filter(([, ms]) => ms > 1000)
      .sort((a, b) => b[1] - a[1]);
    if (slow.length) {
      console.warn(`[game] init took ${(total / 1000).toFixed(1)}s. Over 1s: ` +
        slow.map(([id, ms]) => `${id} ${(ms / 1000).toFixed(1)}s`).join(', '));
    }

    // Placeholder lights/ground, but ONLY for subsystems that have not landed yet. Runs
    // after every init() so it can see which handles are still fallbacks. See
    // fallbacks.js#installDevScaffold for why core owns this rather than each agent.
    installDevScaffold(this.ctx);

    // Late publish: subsystems assign their query handles onto ctx during init().
    installHarness(this);
  }

  /** Resolves when every registered procedural asset has finished building. */
  async settleAssets() {
    let guard = 0;
    while (this._assetPromises.length && guard++ < 12) {
      const batch = this._assetPromises;
      this._assetPromises = [];
      await Promise.all(batch.map((p) => Promise.resolve(p).catch((e) => console.warn('[assets]', e))));
    }
    // Give the GPU a chance to finish compiling.
    if (this.renderer && this.scene && this.camera) {
      try { await this.renderer.compileAsync(this.scene, this.camera); } catch { /* older three */ }
    }
  }

  // ---------------------------------------------------------------- loop

  start() {
    if (this._running || this._destroyed) return;
    this._running = true;
    this._lastTime = performance.now();
    this._accum = 0;
    const tick = (now) => {
      if (!this._running) return;
      this._rafId = requestAnimationFrame(tick);
      this.frame(now);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  stop() {
    this._running = false;
    if (this._rafId) cancelAnimationFrame(this._rafId);
    this._rafId = 0;
  }

  /**
   * True while `ui` is showing the pause overlay. `ui` owns the toggle (it polls the
   * `input.pause` edge in its own update); core owns what that means for the loop.
   *
   * Deliberately tolerant: fallbacks.js keeps `ctx.ui` non-null, but a harness boot can
   * leave a stub with no `paused` getter, and an explicit `=== true` keeps `undefined`
   * from ever reading as paused.
   */
  get paused() {
    return this.ctx.ui?.paused === true;
  }

  /** One rendered frame driven by wall clock. */
  frame(now) {
    const ctx = this.ctx;
    const prof = ctx.profiler;
    prof.beginFrame();

    let delta = (now - this._lastTime) / 1000;
    this._lastTime = now;
    if (!isFinite(delta) || delta < 0) delta = 0;
    if (delta > MAX_FRAME_DELTA) delta = MAX_FRAME_DELTA;
    ctx.time.elapsed += delta;

    if (this._resizePending) this._applySize();

    prof.wrap('input', () => ctx.input.poll());

    if (this.paused) {
      // Frame order step 2a runs and NOTHING else in step 2 does: no fixedUpdate, no
      // bus.flush(), no ctx.time.sim. beginStep() is not optional here — `input.pause` is
      // an edge derived from the key set inside it, so a loop that skipped it could never
      // see the unpause press and the game would wedge paused forever.
      //
      // A tape or driver is excluded because beginStep() advances their cursor, which would
      // consume scripted frames with no step to spend them on. Neither can reach this branch
      // anyway — both pin `pause` to false — so this is belt and braces.
      if (!ctx.input.scripted && !ctx.input.driven) ctx.input.beginStep(SIM_DT);
      // Resume must not replay the pause. Draining rather than freezing the accumulator is
      // the whole point: a 30 s pause would otherwise owe 1800 steps, and MAX_STEPS_PER_FRAME
      // would dribble them out over six seconds of fast-forward. Zero also pins alpha to 0,
      // so the frozen truck draws exactly on its last simulated pose.
      this._accum = 0;
    } else {
      this._accum += delta * ctx.time.scale;
      let steps = 0;
      while (this._accum >= SIM_DT && steps < MAX_STEPS_PER_FRAME) {
        this.fixedStep();
        this._accum -= SIM_DT;
        steps++;
      }
      if (steps === MAX_STEPS_PER_FRAME) this._accum = 0; // shed backlog rather than spiral
    }

    ctx.time.alpha = this._accum / SIM_DT;
    this.presentation(delta);
    this.render();

    ctx.time.frame++;
    prof.endFrame();
    this.renderer.info.reset();
  }

  /** One simulation step. dt is ALWAYS SIM_DT. */
  fixedStep() {
    const ctx = this.ctx;
    const prof = ctx.profiler;
    const dt = SIM_DT;

    ctx.input.beginStep(dt);

    for (const id of FIXED_ORDER) {
      const sys = this.systems.get(id);
      if (!sys || sys.isNull) continue;
      prof.begin(id + '.fixed');
      try { sys.fixedUpdate(dt); } catch (e) { console.error(`[${id}.fixedUpdate]`, e); }
      prof.end();
    }

    prof.begin('bus');
    ctx.bus.flush();
    prof.end();

    ctx.time.sim += dt;
  }

  /** Presentation pass. Must not mutate simulation state. */
  presentation(delta) {
    const ctx = this.ctx;
    const prof = ctx.profiler;
    const dt = this._clocksFrozen ? SIM_DT : delta;
    const alpha = this._clocksFrozen ? 0 : ctx.time.alpha;
    for (const id of UPDATE_ORDER) {
      const sys = this.systems.get(id);
      if (!sys || sys.isNull) continue;
      prof.begin(id + '.update');
      try { sys.update(dt, alpha); } catch (e) { console.error(`[${id}.update]`, e); }
      prof.end();
    }
  }

  /** The ONLY place rendering happens. `look` owns the pass; core falls back if absent. */
  render() {
    const prof = this.ctx.profiler;
    prof.begin('render');
    const look = this.systems.get('look');
    try {
      if (look && !look.isNull && typeof look.render === 'function') look.render();
      else this.renderer.render(this.scene, this.camera);
    } catch (e) {
      console.error('[render]', e);
    }
    prof.end();
  }

  /** Harness: render exactly one frame with no time advance. */
  renderOnce() {
    if (this._resizePending) this._applySize();
    this.ctx.profiler.beginFrame();
    this.ctx.time.alpha = 0;
    this.presentation(0);
    this.render();
    this.ctx.profiler.endFrame();
    this.renderer.info.reset();
  }

  /**
   * Harness: pin all presentation clocks to sim time so a shot is bit-identical.
   * ARCHITECTURE.md §7.3.
   */
  freezeClocks() {
    this._clocksFrozen = true;
    this.ctx.time.alpha = 0;
    for (const sys of this.systems.values()) {
      if (typeof sys.freezeClocks === 'function') { try { sys.freezeClocks(); } catch (e) { console.warn(e); } }
    }
  }

  get clocksFrozen() { return this._clocksFrozen; }

  // ---------------------------------------------------------------- size

  requestResize() { this._resizePending = true; }

  _applySize() {
    this._resizePending = false;
    const forced = this.opts.size;
    const w = forced ? forced.width : (this.canvas.clientWidth || window.innerWidth);
    const h = forced ? forced.height : (this.canvas.clientHeight || window.innerHeight);
    if (w < 2 || h < 2) { this._resizePending = true; return; }

    const dpr = this.isHarness ? 1 : Math.min(window.devicePixelRatio || 1, 2);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, !forced);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();

    const vp = this.ctx.viewport;
    vp.width = w; vp.height = h; vp.aspect = w / h; vp.dpr = dpr;

    for (const sys of this.systems.values()) {
      if (sys.isNull) continue;
      try { sys.onResize(w, h); } catch (e) { console.warn(`[${sys.id}.onResize]`, e); }
    }
  }

  // ---------------------------------------------------------------- teardown

  dispose() {
    this._destroyed = true;
    this.stop();
    disposeDevScaffold(this.ctx);
    for (const sys of this.systems.values()) {
      try { sys.dispose && sys.dispose(); } catch (e) { console.warn(e); }
    }
    this.systems.clear();
    this.ctx.input.dispose();
    this.ctx.bus.clear();
    this.renderer.dispose();
  }
}

/**
 * Minimal MaterialLibrary used only when `look` has not landed yet. Keeps the skeleton
 * booting so world/vehicle/gameplay agents can work before the look pass exists.
 * `look` replaces this entirely — nobody should design against these colours.
 */
function makeFallbackMaterials() {
  const cache = new Map();
  const TINTS = {
    'road.asphalt': 0x3a3a3e, 'road.sidewalk': 0x8a8a86, 'road.curb': 0x9a9a96,
    'road.patch.fresh': 0x1b1b1f, 'road.patch.cured': 0x33333a, 'road.markings': 0xd8d3b0,
    'building.brick': 0x8e5a48, 'building.stone': 0x9c9689, 'building.window': 0x22303c,
    'prop.cone': 0xff5a1e, 'truck.paint': 0xf0a91b, 'traffic.paint': 0x8899aa,
  };
  return {
    isFallback: true,
    async init() {},
    get(key) {
      let m = cache.get(key);
      if (!m) {
        m = new THREE.MeshStandardMaterial({ color: TINTS[key] != null ? TINTS[key] : 0x808080, roughness: 0.9, metalness: 0.0 });
        m.name = 'fallback:' + key;
        cache.set(key, m);
      }
      return m;
    },
    instanceOf(key) { return this.get(key).clone(); },
    uniforms() { return {}; },
    keys() { return Array.from(cache.keys()); },
    dispose() { for (const m of cache.values()) m.dispose(); cache.clear(); },
  };
}
